import React, { useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Button, Chip, Divider, Paper, TextField } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { validateFile, ALLOWED_FILE_TYPES, MAX_FILE_SIZE_MB } from '@/utils/fileValidation';
import { getIndigentTenantRules } from '../tenant/indigentTenantRules';
import { useFilePreview } from '@/hooks/useFilePreview';
import FilePreviewDialog from '@/components/FilePreviewDialog/FilePreviewDialog';
import { useResponsiveToast } from '@/components/Toast';

const uploadFields = [
    { label: 'Certified ID', required: true },
    { label: 'Proof of Address', required: false },
    { label: 'Bank Statement', required: false },
    { label: 'Proof of Pension', required: false },
    { label: 'Income Proof', required: false },
    { label: 'Unemployment Proof', required: false },
    { label: 'Child Headed House Proof', required: false },
    { label: 'Income Below or Equal to R6000', required: false },
    { label: 'Municipal Account Submitted', required: false },
    { label: 'Affidavit of Unemployment', required: false },
    { label: 'Councillor Approval', required: false },
];

const MAX_FILES_PER_FIELD = 3;

type Props = {
    formData: {
        supportingDocs?: { [key: string]: File[] };
        document_types: string[];
        files: File[];
        attachments?: Attachment[];
        deletedAttachmentIds?: string[];
        legacyApplicationNumber?: string;
    };
    setFormData: (data: any) => void;
    onValidationChange: (valid: boolean) => void;
    mode?: 'create' | 'edit';
    isEditable?: boolean;
};

interface Attachment {
    id: string;
    filename: string;
    file_path: string;
    file_type: string;
    document_type: string;
}

const StepSupportingDocuments: React.FC<Props> = ({
    formData,
    setFormData,
    onValidationChange,
    mode = 'create',
    isEditable = true,
}) => {
    const fileInputRefs = useRef<{ [label: string]: HTMLInputElement | null }>({});
    const { isOpen, filePath, fileData, filename, openPreview, openPreviewWithData, closePreview } = useFilePreview();
    const { error: showError } = useResponsiveToast();

    const fieldsWithRequirements = useMemo(() => {
        const rules = getIndigentTenantRules();
        return uploadFields.map(field => {
            if (field.label === 'Certified ID') {
                return { ...field, required: rules.certifiedIdRequired };
            }
            if (field.label === 'Proof of Address') {
                return { ...field, required: rules.proofOfAddressRequired };
            }
            return field;
        });
    }, []);

    // Organize fetched files into supportingDocs structure for edit mode
    const filesMap = useMemo(() => {
        const baseMap = formData.supportingDocs || {};
        
        // In edit mode, organize fetched files by document type
        if (mode === 'edit' && formData.files && formData.files.length > 0) {
            const organizedFiles: { [key: string]: File[] } = { ...baseMap };
            
            // Create a map of document types to files to avoid duplicates
            const docTypeToFiles: { [key: string]: File[] } = {};
            
            formData.files.forEach((file, index) => {
                const docType = formData.document_types[index];
                if (docType) {
                    if (!docTypeToFiles[docType]) {
                        docTypeToFiles[docType] = [];
                    }
                    docTypeToFiles[docType].push(file);
                }
            });
            
            // Convert document types to field labels and organize files
            Object.entries(docTypeToFiles).forEach(([docType, files]) => {
                const fieldLabel = uploadFields.find(field => 
                    field.label.toLowerCase().replace(/\s+/g, '_') === docType
                )?.label;
                
                if (fieldLabel) {
                    organizedFiles[fieldLabel] = files;
                }
            });
            
            return organizedFiles;
        }
        
        return baseMap;
    }, [formData.supportingDocs, formData.files, formData.document_types, mode]);

    // Handle file preview click
    const handleFilePreview = (file: File) => {
        openPreviewWithData(file, file.name);
    };

    // Handle attachment preview click — pass filename with extension so dialog shows image/PDF
    const handleAttachmentPreview = (attachment: Attachment) => {
        const filenameWithExt =
            attachment.file_path?.split('/').pop() ?? attachment.filename ?? 'file';
        openPreview(attachment.file_path, filenameWithExt);
    };

    // Transform data for parent component
    const updateParentData = (newFilesMap: { [key: string]: File[] }) => {
        const allFiles = Object.values(newFilesMap).flat();
        const documentTypes = Object.keys(newFilesMap).map(label =>
            label.toLowerCase().replace(/\s+/g, '_')
        );

        setFormData({
            ...formData,
            supportingDocs: newFilesMap,
            files: allFiles,
            document_types: documentTypes,
        });
    };

    const handleUploadClick = (label: string) => {
        fileInputRefs.current[label]?.click();
    };

    const handleFileChange = (label: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList) return;

        const currentFiles = filesMap[label] || [];
        if (currentFiles.length >= MAX_FILES_PER_FIELD) {
            showError(`Maximum ${MAX_FILES_PER_FIELD} files allowed per field`);
            e.target.value = '';
            return;
        }

        const remainingSlots = MAX_FILES_PER_FIELD - currentFiles.length;
        const selectedFiles = Array.from(fileList).slice(0, remainingSlots);

        // Always validate new file uploads, regardless of mode
        const validFiles = selectedFiles.filter(file => {
            const validationResult = validateFile(file);
            if (!validationResult.isValid) {
                showError(`File "${file.name}": ${validationResult.error?.message}`);
                return false;
            }
            return true;
        });

        if (validFiles.length > 0) {
            const updatedMap = {
                ...filesMap,
                [label]: [...currentFiles, ...validFiles],
            };
            updateParentData(updatedMap);
        }

        e.target.value = ''; // reset
    };

    const handleRemoveFile = (label: string, index: number) => {
        const updated = [...(filesMap[label] || [])];
        updated.splice(index, 1);
        const updatedMap = {
            ...filesMap,
            [label]: updated,
        };
        updateParentData(updatedMap);
    };

    const handleLegacyNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            legacyApplicationNumber: e.target.value,
        });
    };

    // Filter legacy attachments (scanned_document type)
    const legacyAttachments = useMemo(() => {
        if (mode === 'edit' && formData.attachments) {
            return formData.attachments.filter(
                attachment => attachment.document_type === 'scanned_document'
            );
        }
        return [];
    }, [mode, formData.attachments]);

    useEffect(() => {
        // Check if all required fields have at least one file
        // In edit mode, we consider existing attachments as valid
        const hasAllRequiredFiles = fieldsWithRequirements
            .filter(field => field.required)
            .every(field => {
                const hasFiles = filesMap[field.label] && filesMap[field.label].length > 0;
                const hasExistingAttachment =
                    mode === 'edit' &&
                    formData.attachments &&
                    formData.attachments.some(
                        attachment =>
                            attachment.document_type ===
                            field.label.toLowerCase().replace(/\s+/g, '_') &&
                            attachment.document_type !== 'scanned_document' // Exclude legacy attachments from validation
                    );
                return hasFiles || hasExistingAttachment;
            });

        // If we have legacy attachments, we consider the form valid for required fields
        // since legacy data might not have the new document type structure
        const hasLegacyAttachments = mode === 'edit' && legacyAttachments.length > 0;
        const isFormValid = hasAllRequiredFiles || hasLegacyAttachments;

        onValidationChange(isFormValid);
    }, [
        filesMap,
        onValidationChange,
        formData.attachments,
        mode,
        legacyAttachments,
        fieldsWithRequirements,
    ]);

    return (
        <Box>
            {/* Legacy Attachments Section */}
            {legacyAttachments.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography fontSize={16} fontWeight={600} sx={{ mb: 2 }}>
                        Legacy Attachments
                    </Typography>
                    <Typography fontSize={12} color='text.secondary' sx={{ mb: 2 }}>
                        These are existing scanned documents from the previous system.
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                        }}
                    >
                        {legacyAttachments.map((attachment) => (
                            <Chip
                                key={`legacy-${attachment.id}`}
                                label={attachment.filename}
                                icon={<AttachFileIcon />}
                                onClick={() => handleAttachmentPreview(attachment)}
                                onDelete={() => {
                                    // Remove the attachment from the array and track deleted ID
                                    const updatedAttachments =
                                        formData.attachments?.filter(
                                            att => att.id !== attachment.id,
                                        ) || [];
                                    const updatedDeletedIds = [
                                        ...(formData.deletedAttachmentIds || []),
                                        attachment.id,
                                    ];
                                    setFormData({
                                        ...formData,
                                        attachments: updatedAttachments,
                                        deletedAttachmentIds: updatedDeletedIds,
                                    });
                                }}
                                sx={{
                                    backgroundColor: '#F8F9FA',
                                    fontWeight: 400,
                                    fontSize: 14,
                                    borderRadius: 1.5,
                                    px: 1.5,
                                    height: '36px',
                                    minWidth: '120px',
                                    cursor: 'pointer',
                                    border: '1px solid #E9ECEF',
                                    color: '#6C757D',
                                    '&:hover': {
                                        backgroundColor: '#E9ECEF',
                                        borderColor: '#DEE2E6',
                                    },
                                }}
                                deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            <Box sx={{ mb: 3 }}>
                <Typography fontSize={16} fontWeight={600}>
                    Upload Supporting Documents
                </Typography>
                <Typography fontSize={12} color='text.secondary'>
                    Files must be less than {MAX_FILE_SIZE_MB} MB.
                </Typography>
                <Typography fontSize={12} color='text.secondary'>
                    Allowed file types: {ALLOWED_FILE_TYPES.join(', ')}
                </Typography>
            </Box>

            {fieldsWithRequirements.map(field => {
                const existingDocType =
                    formData.document_types && formData.document_types.includes(field.label.toLowerCase().replace(/\s+/g, '_'));
                const hasFiles = filesMap[field.label] && filesMap[field.label].length > 0;

                return (
                    <Box
                        key={field.label}
                        sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        {/* LEFT SIDE: Upload Row */}
                        <Box
                            sx={{
                                backgroundColor: '#FEFBFF',
                                px: 2,
                                py: 1.2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid gray',
                                minHeight: 56,
                                flex: '1 1 0.5px',
                                maxWidth: '360px',
                            }}
                        >
                            <Box>
                                <Typography fontSize={16} fontWeight={500}>
                                    {field.label}{' '}
                                    {field.required && <span style={{ color: '#FF544B' }}>*</span>}
                                </Typography>
                                <Typography variant='caption' color='textSecondary'>
                                    {filesMap[field.label]?.length || 0}/{MAX_FILES_PER_FIELD} files
                                    {existingDocType && !hasFiles && !filesMap[field.label] && ' (existing document)'}
                                </Typography>
                            </Box>
                            <Button
                                variant='text'
                                onClick={() => handleUploadClick(field.label)}
                                disabled={filesMap[field.label]?.length >= MAX_FILES_PER_FIELD || !isEditable}
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: '#0A1D56',
                                    p: 0,
                                    minWidth: 70,
                                    textDecoration: 'underline',
                                    // Override disabled styles to maintain normal appearance
                                    '&.Mui-disabled': {
                                        color: '#0A1D56',
                                        opacity: 1,
                                    },
                                }}
                                endIcon={<AttachFileIcon fontSize='small' />}
                            >
                                Upload
                            </Button>
                            <input
                                type='file'
                                accept={ALLOWED_FILE_TYPES.map(type => `.${type}`).join(',')}
                                hidden
                                multiple
                                disabled={!isEditable}
                                ref={ref => {
                                    fileInputRefs.current[field.label] = ref;
                                }}
                                onChange={e => handleFileChange(field.label, e)}
                            />
                        </Box>

                        {/* RIGHT SIDE: Chips */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                flex: '1 1 320px',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                mt: 1,
                            }}
                        >
                            {/* Show existing attachments from API only if no fetched files exist for this field */}
                            {mode === 'edit' &&
                                formData.attachments &&
                                !filesMap[field.label] && // Only show if no fetched files
                                formData.attachments
                                    .filter(
                                        attachment =>
                                            attachment.document_type ===
                                            field.label.toLowerCase().replace(/\s+/g, '_') &&
                                            attachment.document_type !== 'scanned_document' // Exclude legacy attachments
                                    )
                                    .map((attachment, index) => (
                                        <Chip
                                            key={`existing-${attachment.id}`}
                                            label={attachment.filename}
                                            icon={<AttachFileIcon />}
                                            onClick={() => handleAttachmentPreview(attachment)}
                                            onDelete={() => {
                                                // Remove the attachment from the array and track deleted ID
                                                const updatedAttachments =
                                                    formData.attachments?.filter(
                                                        att => att.id !== attachment.id,
                                                    ) || [];
                                                const updatedDeletedIds = [
                                                    ...(formData.deletedAttachmentIds || []),
                                                    attachment.id,
                                                ];
                                                setFormData({
                                                    ...formData,
                                                    attachments: updatedAttachments,
                                                    deletedAttachmentIds: updatedDeletedIds,
                                                });
                                            }}
                                            sx={{
                                                backgroundColor: '#F9F9FB',
                                                fontWeight: 500,
                                                fontSize: 16,
                                                borderRadius: 2,
                                                px: 2,
                                                height: '48px',
                                                width: '162px',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: '#E3F2FD',
                                                },
                                            }}
                                            deleteIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                                        />
                                    ))}

                            {/* Show existing document type if no files uploaded and no fetched files */}
                            {existingDocType && !hasFiles && !filesMap[field.label] && (
                                <Chip
                                    label={`${field.label} (existing)`}
                                    icon={<AttachFileIcon />}
                                    sx={{
                                        backgroundColor: '#E8F5E8',
                                        fontWeight: 500,
                                        fontSize: 16,
                                        borderRadius: 2,
                                        px: 2,
                                        height: '48px',
                                        width: '162px',
                                        color: '#2E7D32',
                                    }}
                                />
                            )}

                            {/* Show uploaded files (including fetched files) */}
                            {filesMap[field.label]?.map((file: File, index: number) => (
                                <Chip
                                    key={index}
                                    label={file.name}
                                    icon={<AttachFileIcon />}
                                    onClick={() => handleFilePreview(file)}
                                    onDelete={() => handleRemoveFile(field.label, index)}
                                    sx={{
                                        backgroundColor: '#F9F9FB',
                                        fontWeight: 500,
                                        fontSize: 16,
                                        borderRadius: 2,
                                        px: 2,
                                        height: '48px',
                                        width: '162px',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: '#E3F2FD',
                                        },
                                    }}
                                    deleteIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                                />
                            ))}
                        </Box>
                    </Box>
                );
            })}

            {/* Legacy Application Section */}
            <Divider sx={{ my: 4, backgroundColor: '#E4E4E4' }} />

            <Paper elevation={0}>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* LEFT: Legacy Fields */}
                    <Box
                        sx={{
                            flex: '1 1 32px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            maxWidth: '390px',
                        }}
                    >
                        {/* Legacy Application Number Input */}
                        <Box
                            sx={{
                                backgroundColor: '#FEFBFF',
                                px: 2,
                                py: 1.2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid gray',
                                minHeight: 48,
                            }}
                        >
                            <TextField
                                fullWidth
                                label='Legacy Application Number'
                                variant='standard'
                                value={formData.legacyApplicationNumber || ''}
                                onChange={handleLegacyNumberChange}
                                sx={{
                                    '& .MuiInput-root': {
                                        fontSize: 16,
                                        fontWeight: 400,
                                        '&:before, &:after': {
                                            display: 'none',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontSize: 16,
                                        fontWeight: 400,
                                        color: '#6B7280',
                                        '&.Mui-focused': {
                                            color: '#2D4677',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Legacy Document Upload Tile */}
                        <Box
                            sx={{
                                backgroundColor: '#FEFBFF',
                                px: 2,
                                py: 1.2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid gray',
                                minHeight: 48,
                            }}
                        >
                            <Typography fontSize={16} fontWeight={400}>
                                Legacy Document
                            </Typography>
                            <Button
                                variant='text'
                                onClick={() => handleUploadClick('Legacy Document')}
                                disabled={!isEditable}
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: '#0A1D56',
                                    p: 0,
                                    textDecoration: 'underline',
                                    minWidth: 0,
                                    // Override disabled styles to maintain normal appearance
                                    '&.Mui-disabled': {
                                        color: '#0A1D56',
                                        opacity: 1,
                                    },
                                }}
                                endIcon={<AttachFileIcon sx={{ fontSize: 20 }} />}
                            >
                                Upload
                            </Button>
                            <input
                                type='file'
                                hidden
                                accept={ALLOWED_FILE_TYPES.map(type => `.${type}`).join(',')}
                                ref={ref => void (fileInputRefs.current['Legacy Document'] = ref)}
                                onChange={e => handleFileChange('Legacy Document', e)}
                                multiple
                                disabled={!isEditable}
                            />
                        </Box>
                    </Box>

                    {/* RIGHT: Chips */}
                    {filesMap['Legacy Document']?.length > 0 && (
                        <Box
                            sx={{
                                flex: '1 1 320px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                mt: 10.8, // aligns chip vertically with tile
                            }}
                        >
                            {filesMap['Legacy Document'].map((file: File, index: number) => (
                                <Chip
                                    key={index}
                                    label={file.name}
                                    icon={<AttachFileIcon />}
                                    onClick={() => handleFilePreview(file)}
                                    onDelete={() => handleRemoveFile('Legacy Document', index)}
                                    sx={{
                                        backgroundColor: '#F9F9FB',
                                        fontWeight: 500,
                                        fontSize: 16,
                                        borderRadius: '16px',
                                        px: 2,
                                        height: '48px',
                                        width: '162px',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: '#E3F2FD',
                                        },
                                    }}
                                    deleteIcon={<CloseIcon sx={{ fontSize: 20 }} />}
                                />
                            ))}
                        </Box>
                    )}

                    {/* Show existing legacy documents from API */}
                    {mode === 'edit' &&
                        formData.attachments &&
                        !filesMap['Legacy Document'] && // Only show if no fetched files
                        formData.attachments
                            .filter(attachment => 
                                attachment.document_type === 'legacy_document'
                            )
                            .map(attachment => (
                                <Box
                                    key={`existing-legacy-${attachment.id}`}
                                    sx={{
                                        flex: '1 1 320px',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        mt: 10.8, // aligns chip vertically with tile
                                    }}
                                >
                                    <Chip
                                        label={attachment.filename}
                                        icon={<AttachFileIcon />}
                                        onClick={() => handleAttachmentPreview(attachment)}
                                        onDelete={() => {
                                            const updatedAttachments =
                                                formData.attachments?.filter(
                                                    att => att.id !== attachment.id,
                                                ) || [];
                                            const updatedDeletedIds = [
                                                ...(formData.deletedAttachmentIds || []),
                                                attachment.id,
                                            ];
                                            setFormData({
                                                ...formData,
                                                attachments: updatedAttachments,
                                                deletedAttachmentIds: updatedDeletedIds,
                                            });
                                        }}
                                        sx={{
                                            backgroundColor: '#F9F9FB',
                                            fontWeight: 500,
                                            fontSize: 16,
                                            borderRadius: '16px',
                                            px: 2,
                                            height: '48px',
                                            width: '162px',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: '#E3F2FD',
                                            },
                                        }}
                                        deleteIcon={<CloseIcon sx={{ fontSize: 20 }} />}
                                    />
                                </Box>
                            ))}
                </Box>
            </Paper>

            {/* File Preview Dialog */}
            <FilePreviewDialog
                open={isOpen}
                onClose={closePreview}
                filePath={filePath}
                fileData={fileData || undefined}
                filename={filename}
            />
        </Box>
    );
};

export default StepSupportingDocuments;
